import momenttimezone from "moment-timezone";
import api from "@/util/api"
import _ from "lodash";
import i18n from "@/translations/i18n"

const MAX_API_ATTEMPTS = 3;

const eventDefaults = {
  title: "",
  venue: "",
  advisory: "",
  performancesFound: [],
  icAttributesList: [],
  activeDates: [],
  registrationEvent: {},
  sessionsFound: [],
  sessionsStatus: [],
  sessionSelected: {},
  registrationData: {},
  sponsorIccReferences: {},
  previewHash: "",
}

export const state = {
  title: eventDefaults.title,
  venue: eventDefaults.venue,
  advisory: eventDefaults.advisory,
  performancesFound: eventDefaults.performancesFound,
  icAttributesList: eventDefaults.icAttributesList,
  activeDates: eventDefaults.activeDates,
  registrationEvent: eventDefaults.registrationEvent,
  sessionsFound: eventDefaults.sessionsFound,
  sessionsStatus: eventDefaults.sessionsStatus,
  sessionSelected: eventDefaults.sessionsSelected,
  registrationData: eventDefaults.registrationData,
  sponsorIccReferences: eventDefaults.sponsorIccReferences,
}

export const mutations = {
  SET_TITLE(state, title) {
    state.title = title
  },
  SET_VENUE(state, venue) {
    state.venue = venue
  },
  SET_ADVISORY(state, advisory) {
    state.advisory = advisory
  },
  SET_PERFORMANCES_FOUND(state, performances) {
    state.performancesFound = performances
  },
  SET_IC_ATTRIBUTES_LIST(state, attributes) {
    state.icAttributesList = attributes
  },
  SET_ACTIVE_DATES(state, dates) {
    state.activeDates = dates
  },
  SET_REGISTRATION_EVENT(state, event) {
    state.registrationEvent = event
  },
  SET_SESSIONS_FOUND(state, sessions) {
    state.sessionsFound = sessions
  },
  SET_SESSIONS_STATUS(state, sessions) {
    state.sessionsStatus = sessions
  },
  SET_SESSION_SELECTED(state, session) {
    state.sessionSelected = session
  },
  SET_REGISTRATION_DATA(state, data) {
    state.registrationData = data
  },
  SET_PREVIEW_HASH(state, data) {
    state.previewHash = data
  },
  ADD_SPONSOR_REFERENCE(state, item) {
    // Add sponsor details to the reference object
    state.sponsorIccReferences[item.icc] = item.sponsor
    // And then force reactivity (see: https://vuejs.org/v2/guide/reactivity.html#For-Objects)
    state.sponsorIccReferences = Object.assign({}, state.sponsorIccReferences)
  },
  REMOVE_SPONSOR_REFERENCE(state, item) {
    // Clear sponsor details on the reference object
    state.sponsorIccReferences[item.icc] = null
    // And then force reactivity (see: https://vuejs.org/v2/guide/reactivity.html#For-Objects)
    state.sponsorIccReferences = Object.assign({}, state.sponsorIccReferences)
  },
  CLEAR_ALL_SPONSOR_REFERENCES(state) {
    state.sponsorIccReferences = Object.assign({}, {});
  }
}

export const actions = {
  updateSessionSelected({ commit }, session) {
    commit('SET_SESSION_SELECTED', session)
  },
  async resetEventStore({ commit }) {
    commit('SET_TITLE', eventDefaults.title)
    commit('SET_VENUE', eventDefaults.venue)
    commit('CLEAR_ALL_SPONSOR_REFERENCES')
    commit('SET_ADVISORY', eventDefaults.advisory)
    commit('SET_PERFORMANCES_FOUND', eventDefaults.performancesFound)
    commit('SET_IC_ATTRIBUTES_LIST', eventDefaults.icAttributesList)
    commit('SET_ACTIVE_DATES', eventDefaults.activeDates)
    commit('SET_REGISTRATION_EVENT', eventDefaults.registrationEvent)
    commit('SET_SESSIONS_FOUND', eventDefaults.sessionsFound)
    commit('SET_SESSION_SELECTED', eventDefaults.sessionSelected)
    commit('SET_REGISTRATION_DATA', eventDefaults.registrationData)
  },
  async clearRegistrationData({ commit }) {
    commit('SET_REGISTRATION_DATA', eventDefaults.registrationData);
  },

  async setRegistrationData({ state, commit }, payload) {
    let regData = state.registrationData;
    regData[payload.key] = payload.data;
    commit('SET_REGISTRATION_DATA', regData);
  },
  async setPreview({ state, commit }, payload) {
    if (payload !== undefined) {
      commit('SET_PREVIEW_HASH', payload);
    }
  },
  async submitRegistrationData({ state, dispatch }) {
    dispatch('updateLoading', true);

    let eventId = state.sessionSelected.CRMEventID

    try {
      let response = await api.submitEvent(eventId, state.registrationData);

      if (response.status == 200) {
        dispatch('updateLoading', false);
      } else {
        throw response;
      }

      return true;
    } catch (e) {
      dispatch('updateLoading', false);
      let code = _.get(e, 'response.data.error.code');

      if (code) {
        if (code.includes('crm.sip.error')) {
          dispatch('updateError', 'registration_generic_error');
        } else {
          dispatch('updateError', code);
        }
      } else {
        dispatch('updateError', 'registration_submission_failed');
      }

      return false;
    }
  },
  async retrieveDrupalInfo({ state, dispatch, commit }, icc) {
    dispatch('updateLoading', true);

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getDrupalEvent(icc, i18n.locale);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          let drupalContent = _.get(response.data, "drupalEvent");

          // All of the bewlo could be undefined/null
          commit('SET_VENUE', _.get(drupalContent, "venue.title", state.venue));
          commit('SET_TITLE', _.get(drupalContent, "title", state.title));
          commit('ADD_SPONSOR_REFERENCE', { icc: icc, sponsor: drupalContent.sponsor });

          dispatch('updateLoading', false);
        } else {
          throw response;
        }
      } catch (e) {
        failedDrupalInfoRetrieval(e);
      }
    }

    function failedDrupalInfoRetrieval() {
      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);
      }
    }
  },
  async retrieveEventPerformances({ commit, dispatch, state }, icc) {
    dispatch('updateLoading', true);

    let nonRepeatErrors = [
      'error.product.icc.invalid',
    ]

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getEventDatesAndTimes(icc, i18n.locale);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          commit('SET_ADVISORY', _.get(response, "data.data.advisory", state.advisory))
          commit('SET_PERFORMANCES_FOUND', _.get(response, "data.data.showTimingList", state.performancesFound))
          commit('SET_IC_ATTRIBUTES_LIST', _.get(response, "data.data.icAttributesList", state.icAttributesList))

          await dispatch('retrieveDrupalInfo', icc);

          // Default back to sistic information if drupal content is not found.
          if (state.title.length == 0) {
            commit('SET_TITLE', _.get(state.performancesFound[0], "showTitle", state.title));
          }
          if (state.venue.length == 0) {
            commit('SET_VENUE', _.get(state.performancesFound[0], "venue", state.venue));
          }

          let dates = []

          state.performancesFound.forEach((performance) => {
            let date = momenttimezone(performance.showDateTime).tz('Asia/Hong_Kong');
            dates.push(date);
          });

          commit('SET_ACTIVE_DATES', dates);
          dispatch('updateLoading', false);
        } else {
          throw response;
        }
      } catch (e) {
        failedRetrieveEvents(e);
      }
    }

    function failedRetrieveEvents(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (nonRepeatErrors.includes(errorContent)) {
        attempts = MAX_API_ATTEMPTS;
      }

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent);
          dispatch('updateFallbackError', 'generic_error');
        } else {
          dispatch('updateError', 'shopping_cart_submission_failed');
        }
      }
    }
  },
  async retrieveRegistrationSessions({ commit, dispatch, state }, eventId) {
    dispatch('updateLoading', true);

    let attempts = 0

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getDrupalNode(eventId, i18n.locale, state.previewHash);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          commit('SET_REGISTRATION_EVENT', response.data, state.registrationEvent)
          commit('ADD_SPONSOR_REFERENCE', { sponsor: _.get(response.data, "sponsor", null) })

          let sessions = _.get(response, 'data.sessions', state.sessionsFound);
          sessions.sort(function (a, b) {
            return a.date > b.date
          });

          let validSessions = [];
          sessions.forEach((item, index) => {
            // Only display sessions that have a date CRM ID and status of open
            if (_.get(item, 'date') !== null && _.get(item, 'CRMEventID') !== null && _.get(item, 'webform.status') === "open") {
              validSessions.push(item);
            }
          });

          commit('SET_SESSIONS_FOUND', validSessions);

          // Could be optimised and consolidated with this inside retrieveEventPerformances version
          let activeDates = [];

          validSessions.forEach((item, index) => {
            // We re-use the PP show date time page which is driven off the showDateTime
            // quickest way to make it all work is to just populate the attribute with the
            // the contents of the date attribute.
            item.showDateTime = item.date;
            state.sessionsFound[index] = item;
            let date = momenttimezone(item.showDateTime).tz('Asia/Hong_Kong');
            activeDates.push(date);
          });

          commit('SET_ACTIVE_DATES', activeDates);

          let sessionResponse = await api.getSessionData(eventId, i18n.locale, state.previewHash);

          commit('SET_SESSIONS_STATUS', sessionResponse.data)

          dispatch('updateLoading', false);
        } else {
          failedRetrieveSessions(response);
        }
      } catch (e) {
        console.log(e);
        failedRetrieveSessions(e);
      }
    }

    function failedRetrieveSessions(e) {
      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);
        dispatch('updateError', "date_times_retrieval_failed");
      }
    }
  },
}

export const getters = {
  getFormSchema: state => {
    return _.get(state.sessionSelected, 'webform.elements');
  },
}